import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Loader2 } from 'lucide-react';
import { useToast } from './ui/toast';

const EmailCTA = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { addToast } = useToast();

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    // Real-time validation
    if (value && !validateEmail(value)) {
      setErrors({ email: 'Geçerli bir e-posta adresi giriniz' });
    } else {
      setErrors({});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!email) {
      setErrors({ email: 'E-posta adresi gereklidir' });
      return;
    }
    
    if (!validateEmail(email)) {
      setErrors({ email: 'Geçerli bir e-posta adresi giriniz' });
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      if (!response.ok) {
        throw new Error('Abonelik kaydedilemedi');
      }
      
      addToast('Başarıyla kaydedildiniz! Yakında sizinle iletişime geçeceğiz.', 'success');
      setEmail('');
      setErrors({});
      
      // Analytics tracking
      if (window.posthog) {
        window.posthog.capture('email_subscription', { email });
      }
    } catch (error) {
      console.error('Subscription error:', error);
      addToast('Bir hata oluştu. Lütfen tekrar deneyin.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="py-20 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative w-full overflow-hidden rounded-3xl border border-gray-800 bg-gray-900 text-white shadow-2xl"
        >
          {/* Background Image */}
          <img
            src="https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=1920&q=80"
            alt="Background"
            className="absolute inset-0 h-full w-full object-cover opacity-30"
            loading="lazy"
          />
          
          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/80 to-black/70" />

          {/* Content */}
          <div className="relative z-10 grid h-full grid-cols-1 items-center gap-8 p-8 md:grid-cols-2 md:p-12 lg:p-16">
            <div className="flex flex-col items-start text-left text-white">
              <h2 className="text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl leading-tight">
                Bilinçaltı Dönüşüm Haberlerini Kaçırmayın
              </h2>
              <p className="mt-4 max-w-xl text-base md:text-lg text-gray-300 leading-relaxed">
                Eğitimler, özel teknikler ve zihinsel dönüşüm ipuçları hakkında ilk siz haberdar olun. E-posta listemize katılın.
              </p>
            </div>

            <div className="flex w-full max-w-md flex-col items-center justify-center">
              <form onSubmit={handleSubmit} className="w-full space-y-3">
                <div className="relative">
                  <input
                    type="email"
                    placeholder="E-posta adresiniz"
                    value={email}
                    onChange={handleChange}
                    disabled={isLoading}
                    className={`w-full h-12 px-4 rounded-xl border ${
                      errors.email 
                        ? 'border-red-500/50 focus:border-red-500' 
                        : 'border-gray-700 focus:border-purple-500'
                    } bg-gray-800/80 text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                    aria-label="E-posta adresiniz"
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                  />
                  {errors.email && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      id="email-error"
                      className="absolute -bottom-6 left-0 text-red-400 text-xs"
                      role="alert"
                    >
                      {errors.email}
                    </motion.p>
                  )}
                </div>
                
                <button
                  type="submit"
                  disabled={isLoading || !!errors.email}
                  className="group relative w-full h-12 bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 text-white font-light rounded-xl transition-all duration-300 hover:scale-[1.02] shadow-lg hover:shadow-white/10 overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/5 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></span>
                  <span className="relative flex items-center justify-center gap-2">
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Gönderiliyor...
                      </>
                    ) : (
                      <>
                        Hemen Katıl
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                      </>
                    )}
                  </span>
                </button>
              </form>
              
              <p className="mt-4 text-xs text-gray-500 text-center">
                E-posta adresinizi kimseyle paylaşmayacağız. İstediğiniz zaman aboneliğinizden çıkabilirsiniz.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default EmailCTA;